import React, { useEffect, useRef } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import type { MathfieldElement } from 'mathlive';
import 'mathlive';
import SharedMathLiveEditor from '../../components/SharedMathLiveEditor.tsx';
import StepFeedbackMarker from './StepFeedbackMarker.tsx';
import type { WorkingAnswerFeedback } from '../../utils/workingStepFeedback.ts';

type Props = {
  workingSteps: string[];
  draft: string;
  finalAnswer: string;
  onDraftChange: (value: string) => void;
  onCommitStep: (step: string) => void;
  onFinalAnswerChange: (value: string) => void;
  disabled?: boolean;
  unit?: string;
  answerFeedback?: WorkingAnswerFeedback | null;
};

const READONLY_FIELD_STYLE: React.CSSProperties = {
  display: 'block',
  boxSizing: 'border-box',
  fontSize: '1.1rem',
  width: '100%',
  minHeight: '36px',
  maxHeight: '72px',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: '4px 10px',
  overflowX: 'auto',
  overflowY: 'auto',
  backgroundColor: '#f8fafc',
};

const ReadOnlyMathField: React.FC<{ value: string }> = ({ value }) => {
  const ref = useRef<MathfieldElement | null>(null);

  useEffect(() => {
    const field = ref.current;
    if (!field) return;
    const next = value ?? '';
    if (field.value !== next) field.value = next;
  }, [value]);

  return (
    <math-field
      ref={ref as React.RefObject<MathfieldElement>}
      read-only
      data-math-virtual-keyboard-policy="manual"
      style={READONLY_FIELD_STYLE}
    />
  );
};

const WorkingAnswerInput: React.FC<Props> = ({
  workingSteps,
  draft,
  finalAnswer,
  onDraftChange,
  onCommitStep,
  onFinalAnswerChange,
  disabled = false,
  unit,
  answerFeedback = null,
}) => {
  const activeEditorRef = useRef<HTMLDivElement | null>(null);
  const trimmedUnit = (unit ?? '').trim();
  const displayedFinal =
    finalAnswer.trim() ||
    (disabled ? draft.trim() || workingSteps.at(-1)?.trim() || '' : '');

  const commitDraftAsStep = () => {
    const step = draft.trim();
    if (!step) return;
    onCommitStep(step);
    onDraftChange('');
    const field = activeEditorRef.current?.querySelector('math-field') as MathfieldElement | null;
    if (field) field.value = '';
  };

  useEffect(() => {
    const field = activeEditorRef.current?.querySelector('math-field');
    if (!field || disabled) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing) return;
      if (
        event.key !== 'Enter' ||
        event.shiftKey ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }

      const currentValue = ((field as MathfieldElement).value ?? draft).trim();
      if (!currentValue) return;

      event.preventDefault();
      event.stopPropagation();
      onCommitStep(currentValue);
      onDraftChange('');
      (field as MathfieldElement).value = '';
    };

    field.addEventListener('keydown', onKeyDown, true);
    return () => field.removeEventListener('keydown', onKeyDown, true);
  }, [draft, disabled, onCommitStep, onDraftChange]);

  return (
    <Stack spacing={1.5}>
      {workingSteps.map((step, index) => (
        <Stack key={`${index}-${step.slice(0, 24)}`} direction="row" spacing={1} alignItems="flex-start">
          <Typography
            variant="body2"
            sx={{ minWidth: 52, pt: 0.75, color: 'text.secondary', fontWeight: 600 }}
          >
            Step {index + 1}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ReadOnlyMathField value={step} />
          </Box>
          {answerFeedback?.steps[index] && (
            <StepFeedbackMarker
              feedback={answerFeedback.steps[index]}
              label={`Step ${index + 1}`}
            />
          )}
        </Stack>
      ))}

      {!disabled && (
        <>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Working {workingSteps.length === 0 ? '(optional)' : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              Use this for steps. Press Enter or Add step — this is not your marked answer.
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Box ref={activeEditorRef} sx={{ flex: '1 1 220px', minWidth: 0 }} data-disable-next-shortcut="true">
                <SharedMathLiveEditor
                  value={draft}
                  onChange={onDraftChange}
                  minHeightPx={48}
                  maxHeightPx={110}
                  minWidthPx={220}
                  minWidthPercent={100}
                />
              </Box>
              <Button
                type="button"
                variant="outlined"
                size="small"
                disabled={!draft.trim()}
                onClick={commitDraftAsStep}
              >
                Add step
              </Button>
            </Stack>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Final answer
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              This is what gets marked. You can skip working and just fill this in.
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }} data-disable-next-shortcut="true">
                <SharedMathLiveEditor
                  value={finalAnswer}
                  onChange={onFinalAnswerChange}
                  minHeightPx={48}
                  maxHeightPx={110}
                  minWidthPx={220}
                  minWidthPercent={100}
                />
              </Box>
              {trimmedUnit && (
                <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {trimmedUnit}
                </Typography>
              )}
            </Stack>
          </Box>
        </>
      )}

      {disabled && displayedFinal !== '' && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography
            variant="body2"
            sx={{
              minWidth: 52,
              pt: 0.75,
              fontWeight: 600,
              color:
                answerFeedback?.final?.status === 'correct'
                  ? 'success.dark'
                  : answerFeedback?.final?.status === 'wrong'
                    ? 'error.main'
                    : 'text.secondary',
            }}
          >
            Final
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ReadOnlyMathField value={displayedFinal} />
          </Box>
          {answerFeedback?.final && (
            <StepFeedbackMarker feedback={answerFeedback.final} label="Final answer" />
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default WorkingAnswerInput;
